import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import ReactPaginate from "react-paginate";
import "react-toastify/dist/ReactToastify.css";
import OrderModal from "../../components/common/modalComponent/OrderModal/OrderModal";
import {
  validateName,
  validateEmail,
  validatePassword,
  validateNumber,
  validateAddress,
  validatePostcode,
  validateProfileForm,
} from "../../components/common/Validation";
import {
  setPasswordResetError,
  setPasswordResetSuccess,
  setUser,
} from "../../slices/authSlice";
import {
  RESET_PASSWORD_URL,
  PROFILE_EDIT_URL,
  BILLING_UPDATE_URL,
  SHIPPING_UPDATE_URL,
  Image_URL,
} from "../../config/config";
import { fetchOrdersByUserId } from "../../Services/profileServices";
import { fetchReturns } from "../../Services/ReturnServices";
import "./Profile.css";

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get all required auth data from Redux
  const {
    userId: reduxUserId,
    firstName: reduxFirstName,
    lastName: reduxLastName,
    email: reduxEmail,
    accessToken,
    isAuthenticated,
  } = useSelector((state) => state.auth);

  // Auth checking state
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  // Profile state with Redux data
  const [profile, setProfile] = useState({
    firstName: reduxFirstName || "",
    lastName: reduxLastName || "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    postcode: "",
    email: reduxEmail || "",
    contactNumber: "",
  });

  // Billing info state with Redux data
  const [billingInfo, setBillingInfo] = useState({
    firstName: reduxFirstName || "",
    lastName: reduxLastName || "",
    address1: "",
    address2: "",
    city: "",
    country: "",
    province: "",
    postalCode: "",
    phone: "",
    email: reduxEmail || "",
  });

  // Shipping info state with Redux data
  const [shippingInfo, setShippingInfo] = useState({
    firstName: reduxFirstName || "",
    lastName: reduxLastName || "",
    address1: "",
    address2: "",
    city: "",
    country: "",
    province: "",
    postalCode: "",
    phone: "",
    email: reduxEmail || "",
  });

  // Password state
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  // UI states
  const [activeTab, setActiveTab] = useState("Profile");
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [returns, setReturnsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [isShippingLoading, setIsShippingLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Error state
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    postcode: "",
    email: "",
    contactNumber: "",
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 5;

  // Authentication check effect
  useEffect(() => {
    setIsAuthChecking(true);
    if (!isAuthenticated) {
      console.log("User not authenticated, redirecting to login");
      const currentPath = window.location.pathname;
      sessionStorage.setItem("redirectAfterLogin", currentPath);
      navigate("/login");
      return;
    }
    setIsAuthChecking(false);
  }, [isAuthenticated, navigate]);

  // Initial data fetch effect
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!isAuthenticated) return;

      setIsInitialLoading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        };

        // Fetch profile data
        const response = await fetch(`${PROFILE_EDIT_URL}${reduxUserId}`, {
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === 1) {
            setProfile((prev) => ({
              ...prev,
              ...data.data,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
        toast.error("Failed to load profile data");
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [isAuthenticated, reduxUserId, accessToken]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated) return;

      try {
        const orders = await fetchOrdersByUserId(reduxUserId);
        setOrderHistory(orders || []);
      } catch (error) {
        console.error("Error fetching orders:", error);
        toast.error("Failed to load order history");
        setOrderHistory([]);
      }
    };

    // Fetch returns data

    fetchOrders();
  }, [isAuthenticated, reduxUserId]);

  useEffect(() => {
    const fetchReturnData = async () => {
      if (!isAuthenticated) return;

      try {
        const returnsData = await fetchReturns(reduxUserId);
        setReturnsData(returnsData || []);
      } catch (error) {
        console.error("Error fetching returns:", error);
        toast.error("Failed to load returns history");
        setReturnsData([]);
      }
    };

    fetchReturnData();
  }, [isAuthenticated, reduxUserId]);
  // Reset pagination on tab change
  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  // Tab handlers
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleOrderDetails = (order) => {
    navigate(`/orderinformation/${order.InvoiceNumber}`);
  };

  const handleReturnDetails = (order) => {
    navigate(`/returns/${order.InvoiceNumber}/${order.OrderId}`);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // Profile form handlers
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateProfileForm(profile);
    setErrors(newErrors);

    if (Object.values(newErrors).every((error) => error === "")) {
      setIsLoading(true);
      try {
        const payload = {
          userId: reduxUserId,
          fName: profile.firstName,
          lName: profile.lastName,
          email: profile.email,
          status: "1",
          name: `${profile.firstName} ${profile.lastName}`,
        };

        const response = await fetch(PROFILE_EDIT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === 1) {
            dispatch(
              setUser({
                userId: reduxUserId,
                firstName: profile.firstName,
                lastName: profile.lastName,
                email: profile.email,
              })
            );
            toast.success("Profile updated successfully!");
          } else {
            toast.error(data.message || "Failed to update profile");
          }
        }
      } catch (error) {
        console.error("Error updating profile:", error);
        toast.error("An error occurred. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prevProfile) => ({
      ...prevProfile,
      [name]: value,
    }));
  };

  // Password handlers
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {
      currentPassword: validatePassword(passwords.currentPassword),
      newPassword: validatePassword(passwords.newPassword),
      confirmNewPassword:
        passwords.newPassword !== passwords.confirmNewPassword
          ? "New password and confirm new password do not match."
          : "",
    };
    setErrors(newErrors);

    if (Object.values(newErrors).every((error) => error === "")) {
      try {
        const response = await fetch(RESET_PASSWORD_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            currentPassword: passwords.currentPassword,
            newPassword: passwords.newPassword,
            confirmPassword: passwords.confirmNewPassword,
            userId: reduxUserId,
          }),
        });

        const data = await response.json();
        if (response.ok && data.status === 1) {
          dispatch(setPasswordResetSuccess("Password reset successful!"));
          dispatch(setPasswordResetError(""));
          setPasswords({
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          });
          toast.success("Password reset successful!");
        } else {
          dispatch(
            setPasswordResetError(data.message || "Password reset failed")
          );
          dispatch(setPasswordResetSuccess(""));
          toast.error(data.message || "Password reset failed");
        }
      } catch (error) {
        console.error("Error resetting password:", error);
        dispatch(setPasswordResetError("An error occurred. Please try again."));
        dispatch(setPasswordResetSuccess(""));
        toast.error("An error occurred. Please try again.");
      }
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prevPasswords) => ({
      ...prevPasswords,
      [name]: value,
    }));
  };

  // Billing/Shipping handlers
  const handleBillingInfoChange = (e) => {
    const { name, value } = e.target;
    setBillingInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  const handleShippingInfoChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    setIsBillingLoading(true);
    try {
      const payload = {
        id: "1",
        userId: reduxUserId,
        fName: billingInfo.firstName,
        lName: billingInfo.lastName,
        address1: billingInfo.address1,
        address2: billingInfo.address2,
        city: billingInfo.city,
        country: billingInfo.country,
        province: billingInfo.province,
        postalCode: billingInfo.postalCode,
        phone: billingInfo.phone,
        email: billingInfo.email,
      };

      const response = await fetch(BILLING_UPDATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 1) {
          toast.success("Billing information updated successfully!");
        } else {
          toast.error(data.message || "Failed to update billing information");
        }
      }
    } catch (error) {
      console.error("Error updating billing information:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsBillingLoading(false);
    }
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    setIsShippingLoading(true);
    try {
      const payload = {
        id: "2",
        userId: reduxUserId,
        fName: shippingInfo.firstName,
        lName: shippingInfo.lastName,
        address1: shippingInfo.address1,
        address2: shippingInfo.address2,
        city: shippingInfo.city,
        country: shippingInfo.country,
        province: shippingInfo.province,
        postalCode: shippingInfo.postalCode,
        phone: shippingInfo.phone,
        email: shippingInfo.email,
      };

      const response = await fetch(SHIPPING_UPDATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 1) {
          toast.success("Shipping information updated successfully!");
        } else {
          toast.error(data.message || "Failed to update shipping information");
        }
      }
    } catch (error) {
      console.error("Error updating shipping information:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsShippingLoading(false);
    }
  };

  // Utility functions
  const formatDate = (dateString) => {
    return dateString.split(" ")[0];
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 0:
        return "in-progress";
      case 1:
        return "delivered";
      default:
        return "";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "0":
        return "In Progress";
      case "1":
        return "Delivered";
      default:
        return "Unknown";
    }
  };

  const getCategoryName = (categoryId) => {
    const categoryMap = {
      8: "Women",
      9: "Men",
    };
    return categoryMap[categoryId] || "Unknown";
  };

  const getHeading = () => {
    switch (activeTab) {
      case "Profile":
        return "User Profile";
      case "Order History":
        return "Order History";
      case "Settings":
        return "Settings";
      case "Billing Info":
        return "Billing Info";
      case "Shipping Info":
        return "Shipping Info";
      case "Returns":
        return "Returns";
      default:
        return "User Profile";
    }
  };

  // Data handling
  const getDataForActiveTab = () => {
    switch (activeTab) {
      case "Order History":
        return orderHistory;
      case "Returns":
        return returns;
      default:
        return [];
    }
  };

  const dataForCurrentTab = getDataForActiveTab();
  const pageCount = Math.ceil(dataForCurrentTab.length / ITEMS_PER_PAGE);
  const offset = currentPage * ITEMS_PER_PAGE;
  const currentItems = dataForCurrentTab.slice(offset, offset + ITEMS_PER_PAGE);

  // Fixed handlePageChange function
  const handlePageChange = (event) => {
    setCurrentPage(event.selected);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // if (isInitialLoading) {
  //   return <Spinner />;
  // }

  return (
    <div className="user-profile">
      <h1>{getHeading()}</h1>
      <hr className="profile-divider" />
      <div className="profile-container">
        <div className="sidebar-container">
          <div className="sidebar">
            <ul>
              <li
                className={activeTab === "Profile" ? "active" : ""}
                onClick={() => handleTabClick("Profile")}
              >
                <i className="fa-solid fa-user"></i> Profile
              </li>
              <li
                className={activeTab === "Order History" ? "active" : ""}
                onClick={() => handleTabClick("Order History")}
              >
                <i className="fa-solid fa-store"></i> Order History
              </li>
              <li
                className={activeTab === "Settings" ? "active" : ""}
                onClick={() => handleTabClick("Settings")}
              >
                <i className="fa-solid fa-gear"></i> Settings
              </li>
              <li
                className={activeTab === "Billing Info" ? "active" : ""}
                onClick={() => handleTabClick("Billing Info")}
              >
                <i className="fa-solid fa-file-invoice-dollar"></i> Billing Info
              </li>
              <li
                className={activeTab === "Shipping Info" ? "active" : ""}
                onClick={() => handleTabClick("Shipping Info")}
              >
                <i className="fa-solid fa-truck"></i> Shipping Info
              </li>
              <li
                className={activeTab === "Returns" ? "active" : ""}
                onClick={() => handleTabClick("Returns")}
              >
                <i className="fa-solid fa-file-alt"></i> Returns
              </li>
            </ul>
          </div>
          <ul className="sidebar-chips">
            <li
              className={`chip ${activeTab === "Profile" ? "active" : ""}`}
              onClick={() => handleTabClick("Profile")}
            >
              Profile
            </li>
            <li
              className={`chip ${
                activeTab === "Order History" ? "active" : ""
              }`}
              onClick={() => handleTabClick("Order History")}
            >
              Order History
            </li>
            <li
              className={`chip ${activeTab === "Settings" ? "active" : ""}`}
              onClick={() => handleTabClick("Settings")}
            >
              Settings
            </li>
            <li
              className={`chip ${activeTab === "Billing Info" ? "active" : ""}`}
              onClick={() => handleTabClick("Billing Info")}
            >
              Billing Info
            </li>
            <li
              className={`chip ${
                activeTab === "Shipping Info" ? "active" : ""
              }`}
              onClick={() => handleTabClick("Shipping Info")}
            >
              Shipping Info
            </li>
            <li
              className={`chip ${activeTab === "Returns" ? "active" : ""}`}
              onClick={() => handleTabClick("Returns")}
            >
              Returns
            </li>
          </ul>
        </div>
        <div className="personal-info-container">
          {activeTab === "Profile" && (
            <div className="profile-form">
              <h2>Personal Information</h2>
              <form onSubmit={handleProfileSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="Kevin"
                      value={profile.firstName}
                      onChange={handleProfileChange}
                    />
                    {errors.firstName && (
                      <p className="error">{errors.firstName}</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Paul"
                      value={profile.lastName}
                      onChange={handleProfileChange}
                    />
                    {errors.lastName && (
                      <p className="error">{errors.lastName}</p>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Address Line 1</label>
                    <input
                      type="text"
                      name="addressLine1"
                      placeholder="32/A , Sea View, Galle Road"
                      value={profile.addressLine1}
                      onChange={handleProfileChange}
                    />
                    {errors.addressLine1 && (
                      <p className="error">{errors.addressLine1}</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="addressLine2"
                      placeholder="Colombo 03"
                      value={profile.addressLine2}
                      onChange={handleProfileChange}
                    />
                    {errors.addressLine2 && (
                      <p className="error">{errors.addressLine2}</p>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="city"
                      placeholder="Kollupitiya"
                      value={profile.city}
                      onChange={handleProfileChange}
                    />
                    {errors.city && <p className="error">{errors.city}</p>}
                  </div>
                  <div className="form-group">
                    <label>Postcode</label>
                    <input
                      type="text"
                      name="postcode"
                      placeholder="10160"
                      value={profile.postcode}
                      onChange={handleProfileChange}
                    />
                    {errors.postcode && (
                      <p className="error">{errors.postcode}</p>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="kevin@gmail.com"
                      value={profile.email}
                      onChange={handleProfileChange}
                    />
                    {errors.email && <p className="error">{errors.email}</p>}
                  </div>
                  <div className="form-group">
                    <label>Contact Number</label>
                    <input
                      type="text"
                      name="contactNumber"
                      placeholder="0767893678"
                      value={profile.contactNumber}
                      onChange={handleProfileChange}
                    />
                    {errors.contactNumber && (
                      <p className="error">{errors.contactNumber}</p>
                    )}
                  </div>
                </div>
                <button
                  type="submit"
                  className="save-button"
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Save Changes"}
                </button>
              </form>
            </div>
          )}

          {activeTab === "Order History" && (
            <div className="order-container">
              <div className="order-history">
                <h2>Order History</h2>
                {dataForCurrentTab.length === 0 ? (
                  <div className="empty-state">
                    <i className="fa-solid fa-box-open"></i>
                    <p>You have no orders yet.</p>
                  </div>
                ) : (
                  <>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Status</th>
                          <th>Price</th>
                          <th>Category</th>
                          <th>Order Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((order) => {
                          const orderDetail = order.OrderDetails?.[0];
                          const dateDifference = Math.floor(
                            (new Date() - new Date(order.DateCreated)) /
                              (1000 * 60 * 60 * 24)
                          );
                          const isReturnDisabled = dateDifference > 14;

                          return (
                            <tr key={order.OrderId}>
                              <td data-label="Product" className="product-td">
                                <div className="product-details">
                                  {orderDetail && (
                                    <>
                                      <img
                                        src={`${Image_URL}products/thumbnails/${
                                          orderDetail.Sources?.split(",")[0]
                                        }`}
                                        alt="Product"
                                        className="product-image"
                                        onClick={() =>
                                          handleOrderDetails(order)
                                        }
                                      />
                                      <div className="Profile-product-normal">
                                        <span className="product-name">
                                          {orderDetail.Title}
                                        </span>
                                        <span className="product-id">
                                          #{orderDetail.ProductId}
                                        </span>
                                      </div>
                                      <div className="Profile-product-name">
                                        {orderDetail.Title}
                                        <br />
                                        <span className="Profile-product-id">
                                          #{orderDetail.ProductId}
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td
                                data-label="Status:"
                                className="order-info-item"
                              >
                                <span
                                  className={`status ${getStatusClass(
                                    order.OrderStatus
                                  )}`}
                                >
                                  {getStatusText(order.OrderStatus)}
                                </span>
                              </td>
                              <td
                                data-label="Price:"
                                className="order-info-item"
                              >
                                Rs. {order.OrderAmount.toLocaleString()}
                              </td>
                              <td
                                data-label="Category:"
                                className="order-info-item"
                              >
                                {orderDetail
                                  ? getCategoryName(orderDetail.CategoryId)
                                  : "Unknown"}
                              </td>
                              <td
                                data-label="Order Date:"
                                className="order-info-item"
                              >
                                {formatDate(order.DateCreated)}
                              </td>
                              <td className="actions-td">
                                <div className="dropdown dropdown-container desktop-only">
                                  <button
                                    className="btn btn-secondary dropdown-toggle more-info"
                                    type="button"
                                    id={`dropdownMenuButton${order.OrderId}`}
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <i className="fa-solid fa-ellipsis-vertical"></i>
                                  </button>
                                  <ul
                                    className="dropdown-menu"
                                    aria-labelledby={`dropdownMenuButton${order.OrderId}`}
                                  >
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() =>
                                          handleOrderDetails(order)
                                        }
                                      >
                                        View Order
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className={`dropdown-item ${
                                          isReturnDisabled ? "disabled" : ""
                                        }`}
                                        onClick={() =>
                                          !isReturnDisabled &&
                                          handleReturnDetails(order)
                                        }
                                        disabled={isReturnDisabled}
                                      >
                                        Return product
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                                <hr className="order-border" />
                                <div className="action-chips mobile-only">
                                  <button
                                    className="chip"
                                    onClick={() => handleOrderDetails(order)}
                                  >
                                    View Order
                                  </button>
                                  {!isReturnDisabled && (
                                    <button
                                      className="chip"
                                      onClick={() => handleReturnDetails(order)}
                                    >
                                      Return Product
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {dataForCurrentTab.length > ITEMS_PER_PAGE && (
                      <div className="react-paginate-container">
                        <ReactPaginate
                          previousLabel={
                            <i className="fa-solid fa-chevron-left"></i>
                          }
                          nextLabel={
                            <i className="fa-solid fa-chevron-right"></i>
                          }
                          breakLabel="..."
                          pageCount={pageCount}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="react-paginate"
                          pageClassName="paginate-page"
                          pageLinkClassName="paginate-link"
                          previousClassName="paginate-page"
                          previousLinkClassName="paginate-link"
                          nextClassName="paginate-page"
                          nextLinkClassName="paginate-link"
                          breakClassName="paginate-page"
                          breakLinkClassName="paginate-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === "Settings" && (
            <div className="settings-form">
              <h2>Change Password</h2>
              <form onSubmit={handlePasswordSubmit}>
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    placeholder="****"
                    value={passwords.currentPassword}
                    onChange={handlePasswordChange}
                  />
                  {errors.currentPassword && (
                    <p className="error">{errors.currentPassword}</p>
                  )}
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    placeholder="****"
                    value={passwords.newPassword}
                    onChange={handlePasswordChange}
                  />
                  {errors.newPassword && (
                    <p className="error">{errors.newPassword}</p>
                  )}
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmNewPassword"
                    placeholder="****"
                    value={passwords.confirmNewPassword}
                    onChange={handlePasswordChange}
                  />
                  {errors.confirmNewPassword && (
                    <p className="error">{errors.confirmNewPassword}</p>
                  )}
                </div>
                <button type="submit" className="save-button">
                  Change Password
                </button>
              </form>
            </div>
          )}

          {activeTab === "Billing Info" && (
            <div className="billing-info">
              <h2>Billing Information</h2>
              <form onSubmit={handleBillingSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={billingInfo.firstName}
                      onChange={handleBillingInfoChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={billingInfo.lastName}
                      onChange={handleBillingInfoChange}
                      placeholder="Last name"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Address Line 1</label>
                    <input
                      type="text"
                      name="address1"
                      value={billingInfo.address1}
                      onChange={handleBillingInfoChange}
                      placeholder="Address Line 1"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="address2"
                      value={billingInfo.address2}
                      onChange={handleBillingInfoChange}
                      placeholder="Address Line 2 (optional)"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="city"
                      value={billingInfo.city}
                      onChange={handleBillingInfoChange}
                      placeholder="City"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={billingInfo.country}
                      onChange={handleBillingInfoChange}
                      placeholder="Country"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Province</label>
                    <input
                      type="text"
                      name="province"
                      value={billingInfo.province}
                      onChange={handleBillingInfoChange}
                      placeholder="Province"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={billingInfo.postalCode}
                      onChange={handleBillingInfoChange}
                      placeholder="Postal Code"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={billingInfo.phone}
                      onChange={handleBillingInfoChange}
                      placeholder="Phone"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={billingInfo.email}
                      onChange={handleBillingInfoChange}
                      placeholder="Email"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="save-button"
                  disabled={isBillingLoading}
                >
                  {isBillingLoading ? "Saving..." : "Save Changes"}
                </button>
              </form>
            </div>
          )}

          {activeTab === "Shipping Info" && (
            <div className="shipping-info">
              <h2>Shipping Information</h2>
              <form onSubmit={handleShippingSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={shippingInfo.firstName}
                      onChange={handleShippingInfoChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={shippingInfo.lastName}
                      onChange={handleShippingInfoChange}
                      placeholder="Last name"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Address Line 1</label>
                    <input
                      type="text"
                      name="address1"
                      value={shippingInfo.address1}
                      onChange={handleShippingInfoChange}
                      placeholder="Address Line 1"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="address2"
                      value={shippingInfo.address2}
                      onChange={handleShippingInfoChange}
                      placeholder="Address Line 2 (optional)"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="city"
                      value={shippingInfo.city}
                      onChange={handleShippingInfoChange}
                      placeholder="City"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={shippingInfo.country}
                      onChange={handleShippingInfoChange}
                      placeholder="Country"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Province</label>
                    <input
                      type="text"
                      name="province"
                      value={shippingInfo.province}
                      onChange={handleShippingInfoChange}
                      placeholder="Province"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={shippingInfo.postalCode}
                      onChange={handleShippingInfoChange}
                      placeholder="Postal Code"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={shippingInfo.phone}
                      onChange={handleShippingInfoChange}
                      placeholder="Phone"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={shippingInfo.email}
                      onChange={handleShippingInfoChange}
                      placeholder="Email"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="save-button"
                  disabled={isShippingLoading}
                >
                  {isShippingLoading ? "Saving..." : "Save Changes"}
                </button>
              </form>
            </div>
          )}

          {activeTab === "Returns" && (
            <div className="order-container">
              <div className="order-history">
                <h2>Return History</h2>
                {dataForCurrentTab.length === 0 ? (
                  <div className="empty-state">
                    <i className="fa-solid fa-undo-alt"></i>
                    <p>You have no returns yet.</p>
                  </div>
                ) : (
                  <>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Invoice</th>
                          <th>Status</th>
                          <th>Price</th>
                          <th>Date Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((returns) => (
                          <tr key={returns.ReturnId}>
                            <td data-label="Product" className="product-td">
                              <div className="product-details">
                                <img
                                  src={`${Image_URL}products/thumbnails/${
                                    returns?.Colors?.[0]?.Sources?.split(
                                      ","
                                    )[0] || ""
                                  }`}
                                  alt={returns?.ProductName || ""}
                                  className="product-image"
                                />
                                <div className="Profile-product-normal">
                                  <span className="product-name">
                                    {returns.ProductName}
                                  </span>
                                  <span className="product-id">
                                    #{returns.ProductId}
                                  </span>
                                </div>
                                <div className="Profile-product-name">
                                  {returns.ProductName}
                                  <br />
                                  <span className="Profile-product-id">
                                    #{returns.ProductId}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td
                              data-label="Invoice"
                              className="order-info-item"
                            >
                              {orderHistory.find(order => order.OrderId === returns.OrderId)?.InvoiceNumber || returns.OrderId}
                            </td>
                            <td data-label="Status" className="order-info-item">
                              <span
                                className={`status ${returns.Status.toLowerCase()}`}
                              >
                                {returns.Status}
                              </span>
                            </td>
                            <td data-label="Price" className="order-info-item">
                              Rs. {Number(returns.Price).toLocaleString()}
                            </td>
                            <td
                              data-label="Date Created"
                              className="order-info-item"
                            >
                              {formatDate(returns.DateCreated)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {dataForCurrentTab.length > ITEMS_PER_PAGE && (
                      <div className="react-paginate-container">
                        <ReactPaginate
                          previousLabel={
                            <i className="fa-solid fa-chevron-left"></i>
                          }
                          nextLabel={
                            <i className="fa-solid fa-chevron-right"></i>
                          }
                          breakLabel="..."
                          pageCount={pageCount}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="react-paginate"
                          pageClassName="paginate-page"
                          pageLinkClassName="paginate-link"
                          previousClassName="paginate-page"
                          previousLinkClassName="paginate-link"
                          nextClassName="paginate-page"
                          nextLinkClassName="paginate-link"
                          breakClassName="paginate-page"
                          breakLinkClassName="paginate-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {showModal && selectedOrder && (
        <OrderModal order={selectedOrder} onClose={closeModal} />
      )}
      <ToastContainer />
    </div>
  );
};

export default UserProfile;
